import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Download } from "lucide-react";
import { downloadCsv } from "@/components/admin/AdminUsers";

export const AdminStats = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  if (error) return <p className="text-sm font-bold py-8 text-center">Impossibile caricare le statistiche.</p>;
  if (!data)
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 rounded-3xl" />
        <Skeleton className="h-40 rounded-3xl" />
      </div>
    );

  const worstBlock = data.blockFailRates.filter((b) => b.failRate !== null).sort((a, b) => (b.failRate || 0) - (a.failRate || 0))[0];

  return (
    <div data-testid="admin-stats-panel">
      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3">
        {[
          ["Iscritti totali", data.totalUsers, ""],
          ["Attivi (30 gg)", data.activeUsers30d, ""],
          ["Completamento medio", `${data.avgCompletionPercent}%`, ""],
          ["Tasso di idoneità", data.eligibilityRate === null ? "—" : `${data.eligibilityRate}%`, `${data.fullExamAttempts} esami completi`],
        ].map(([label, val, sub]) => (
          <div key={label} className="rounded-3xl border border-[color:var(--hairline)] bg-white p-4 text-center">
            <p className="font-mono text-2xl font-semibold tracking-tight">{val}</p>
            <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-2)] font-bold mt-1">{label}</p>
            {sub && <p className="font-mono text-[10px] text-[color:var(--text-2)] mt-0.5">{sub}</p>}
          </div>
        ))}
      </div>

      {/* Block fail rates */}
      <div className="mt-4 rounded-3xl border border-[color:var(--hairline)] bg-white p-5" data-testid="admin-block-fail-rates">
        <h4 className="text-xs font-extrabold tracking-[0.14em] uppercase">Dove cadono gli studenti</h4>
        <p className="text-xs text-[color:var(--text-2)] mt-1 mb-4">% di esami completi con blocco sotto 9/15.</p>
        <div className="space-y-3">
          {data.blockFailRates.map((b) => (
            <div key={b.blocco}>
              <div className="flex items-center justify-between text-sm">
                <p className="font-semibold">
                  <span className="font-mono text-[11px] text-[color:var(--text-2)] uppercase mr-2">B{b.blocco}</span>
                  {b.nome}
                </p>
                <span className="font-mono text-sm font-semibold">{b.failRate === null ? "—" : `${b.failRate}%`}</span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-[color:var(--bg-subtle)] overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${b.failRate || 0}%`, background: worstBlock && b.blocco === worstBlock.blocco && b.failRate ? "var(--danger)" : "var(--ink)" }}
                />
              </div>
            </div>
          ))}
        </div>
        {worstBlock && worstBlock.failRate > 0 && (
          <p className="mt-4 pt-3 border-t border-[color:var(--hairline)] text-sm">
            <strong>Il blocco che fa cadere di più:</strong> <span className="text-[color:var(--text-2)]">{worstBlock.nome} (Blocco {worstBlock.blocco}, {worstBlock.failRate}%)</span>
          </p>
        )}
      </div>

      {/* Hardest questions */}
      <div className="mt-4 rounded-3xl border border-[color:var(--hairline)] bg-white p-5" data-testid="admin-hardest-questions">
        <h4 className="text-xs font-extrabold tracking-[0.14em] uppercase">Domande con più errori</h4>
        <p className="text-xs text-[color:var(--text-2)] mt-1 mb-4">Utile per migliorare contenuti e spiegazioni (minimo 3 risposte registrate).</p>
        {data.hardestQuestions.length === 0 && (
          <p className="text-sm text-[color:var(--text-2)]">Ancora pochi dati: serviranno più simulazioni.</p>
        )}
        <div className="space-y-2">
          {data.hardestQuestions.map((q, i) => (
            <div key={q.questionId} className="flex items-start gap-3 rounded-2xl border border-[color:var(--hairline)] px-3.5 py-2.5">
              <span className="font-mono text-xs font-semibold text-[color:var(--text-2)] pt-0.5 w-5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-5 line-clamp-2">{q.testo}</p>
                <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-0.5">Blocco {q.blocco} · {q.attempts} risposte</p>
              </div>
              <span className="font-mono text-sm font-semibold text-[color:var(--danger)] shrink-0">{q.errorRate}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Export */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => downloadCsv("/admin/export/results", "risultati.csv")}
          data-testid="admin-export-results-button"
          className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 hover:bg-black/85 transition-colors duration-200"
        >
          <Download className="w-3.5 h-3.5" /> Export risultati CSV
        </button>
        <button
          onClick={() => downloadCsv("/admin/export/users", "iscritti.csv")}
          data-testid="admin-export-users-stats-button"
          className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] text-xs font-bold px-4 py-2.5 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
        >
          <Download className="w-3.5 h-3.5" /> Export iscritti CSV
        </button>
      </div>
    </div>
  );
};
